import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

type Op = '+' | '-' | '*' | '/';

interface Puzzle {
  operators: Op[];
  operands: number[];
  target: number;
}

interface Level {
  level: number;
  objective: string;
  puzzles: Puzzle[];
}

const LEVELS = levelsData.levels as Level[];
const OP_LABEL: Record<Op, string> = { '+': '+', '-': '−', '*': '×', '/': '÷' };

interface Token {
  type: 'num' | 'op';
  value: string;
  /** índice no pool de operandos (apenas pra type=num) — pra liberar ao limpar */
  operandIdx?: number;
}

/** Avalia expressão com precedência (×÷ antes de +−), parsing simples sem parênteses. */
function evalExpression(tokens: Token[]): number | null {
  if (tokens.length === 0 || tokens[0]!.type !== 'num') return null;
  if (tokens[tokens.length - 1]!.type !== 'num') return null;
  // Esperado: alternância num, op, num, op, num...
  for (let i = 0; i < tokens.length; i++) {
    if (i % 2 === 0 && tokens[i]!.type !== 'num') return null;
    if (i % 2 === 1 && tokens[i]!.type !== 'op') return null;
  }
  // Primeiro passe: ×÷ (esquerda → direita)
  const stack: Token[] = [tokens[0]!];
  for (let i = 1; i < tokens.length; i += 2) {
    const op = tokens[i]!.value as Op;
    const right = Number(tokens[i + 1]!.value);
    if (op === '*' || op === '/') {
      const left = Number(stack.pop()!.value);
      if (op === '/' && (right === 0 || left % right !== 0)) return null;
      const r = op === '*' ? left * right : left / right;
      stack.push({ type: 'num', value: String(r) });
    } else {
      stack.push({ type: 'op', value: op });
      stack.push({ type: 'num', value: String(right) });
    }
  }
  // Segundo passe: +− (esquerda → direita)
  let acc = Number(stack[0]!.value);
  for (let i = 1; i < stack.length; i += 2) {
    const op = stack[i]!.value as Op;
    const right = Number(stack[i + 1]!.value);
    acc = op === '+' ? acc + right : acc - right;
  }
  return acc;
}

export function mountGuesscount(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0]!;
  const totalPuzzles = level.puzzles.length;

  let puzzleIdx = 0;
  let tokens: Token[] = [];
  let usedOperands = new Set<number>();
  let correctCount = 0;
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Adivinhe a conta</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage guesscount">
      <div class="feedback" aria-live="polite"></div>
      <p class="guesscount__progress" aria-live="polite"></p>
      <div class="guesscount__target" aria-live="polite"></div>
      <div class="guesscount__expr" aria-live="polite"></div>
      <p class="guesscount__hint">Toque nos números e operadores pra montar a conta.</p>
      <div class="guesscount__pool guesscount__pool--operands" role="group" aria-label="Números"></div>
      <div class="guesscount__pool guesscount__pool--ops" role="group" aria-label="Operadores"></div>
      <div class="guesscount__actions">
        <button class="btn btn--secondary guesscount__reset" type="button">↺ Limpar</button>
        <button class="btn btn--primary guesscount__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.guesscount__progress')!;
  const targetEl = page.querySelector<HTMLElement>('.guesscount__target')!;
  const exprEl = page.querySelector<HTMLElement>('.guesscount__expr')!;
  const operandPoolEl = page.querySelector<HTMLElement>('.guesscount__pool--operands')!;
  const opPoolEl = page.querySelector<HTMLElement>('.guesscount__pool--ops')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const resetBtn = page.querySelector<HTMLButtonElement>('.guesscount__reset')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.guesscount__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  resetBtn.addEventListener('click', () => { if (!buttonsBlocked) resetExpression(); });
  confirmBtn.addEventListener('click', validate);

  function resetExpression(): void {
    tokens = [];
    usedOperands = new Set();
    ctx.audio.play('click');
    render();
  }

  function startPuzzle(): void {
    tokens = [];
    usedOperands = new Set();
    buttonsBlocked = false;
    render();
  }

  function render(): void {
    const p = level.puzzles[puzzleIdx]!;
    progressEl.textContent = `Pergunta ${puzzleIdx + 1} de ${totalPuzzles}`;
    targetEl.innerHTML = `Chegue em <strong>${p.target}</strong>`;
    exprEl.textContent = tokens.length
      ? tokens.map((t) => (t.type === 'op' ? OP_LABEL[t.value as Op] : t.value)).join(' ')
      : '— ?';

    // pool operandos
    operandPoolEl.innerHTML = '';
    p.operands.forEach((n, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'guesscount__chip guesscount__chip--num';
      if (usedOperands.has(i)) btn.classList.add('guesscount__chip--used');
      btn.textContent = String(n);
      btn.disabled = usedOperands.has(i) || lastIsNumber();
      btn.addEventListener('click', () => addOperand(n, i));
      operandPoolEl.appendChild(btn);
    });

    // pool operadores
    opPoolEl.innerHTML = '';
    p.operators.forEach((op) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'guesscount__chip guesscount__chip--op';
      btn.textContent = OP_LABEL[op];
      btn.disabled = !canAddOperator();
      btn.addEventListener('click', () => addOperator(op));
      opPoolEl.appendChild(btn);
    });
  }

  function lastIsNumber(): boolean {
    return tokens.length > 0 && tokens[tokens.length - 1]!.type === 'num';
  }

  function canAddOperator(): boolean {
    return lastIsNumber();
  }

  function addOperand(value: number, idx: number): void {
    if (buttonsBlocked) return;
    if (lastIsNumber()) return;
    tokens.push({ type: 'num', value: String(value), operandIdx: idx });
    usedOperands.add(idx);
    ctx.audio.play('click');
    render();
  }

  function addOperator(op: Op): void {
    if (buttonsBlocked) return;
    if (!canAddOperator()) return;
    tokens.push({ type: 'op', value: op });
    ctx.audio.play('click');
    render();
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function validate(): void {
    if (buttonsBlocked) return;
    const p = level.puzzles[puzzleIdx]!;
    if (tokens.length === 0 || tokens[tokens.length - 1]!.type === 'op') {
      ctx.audio.play('wrong');
      showFeedback('err');
      return;
    }
    const result = evalExpression(tokens);
    if (result !== null && result === p.target) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      correctCount++;
      puzzleIdx++;
      window.setTimeout(() => {
        if (puzzleIdx < totalPuzzles) startPuzzle();
        else finishLevel();
      }, 800);
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
      exprEl.classList.add('guesscount__expr--wrong');
      window.setTimeout(() => exprEl.classList.remove('guesscount__expr--wrong'), 500);
    }
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, correctCount);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correctCount} de ${totalPuzzles}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) next.addEventListener('click', () => { window.location.hash = `/guesscount/${level.level + 1}`; });
  }

  startPuzzle();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
